import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Nav } from "../components/Nav";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Separator } from "../components/ui/separator";
import { Logo } from "../components/Logo";

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const pendingId = searchParams.get("pending");
    const ssoError = searchParams.get("sso_error");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState(
        ssoError ? `SocialPulse sign-in failed (${ssoError}). Please try again.` : "",
    );
    const [loading, setLoading] = useState(false);

    const activatePending = async () => {
        if (!pendingId) return null;
        try {
            const { data } = await api.post(`/preview/${encodeURIComponent(pendingId)}/activate`);
            return data;
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not activate your preview link");
            return null;
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        const res = await login(email, password);
        if (res.ok) {
            const activated = await activatePending();
            setLoading(false);
            if (activated) toast.success(`Activated /${activated.slug}`);
            navigate("/dashboard");
        } else {
            setLoading(false);
            setError(res.error);
        }
    };

    const googleLogin = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/dashboard" + (pendingId ? `?pending=${encodeURIComponent(pendingId)}` : "");
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    const socialpulseLogin = () => {
        const backend = process.env.REACT_APP_BACKEND_URL;
        const params = new URLSearchParams();
        if (pendingId) params.set("pending", pendingId);
        params.set("next", "/dashboard");
        window.location.href = `${backend}/api/auth/socialpulse/start?${params.toString()}`;
    };

    return (
        <div className="App min-h-screen">
            <Nav />
            <div className="mx-auto grid min-h-[calc(100vh-4rem)] max-w-7xl grid-cols-1 lg:grid-cols-2">
                <aside className="hidden border-r border-border bg-[hsl(224,15%,7%)] p-12 text-[hsl(210,20%,98%)] lg:flex lg:flex-col lg:justify-between">
                    <Logo textClass="text-2xl text-white" />
                    <div>
                        <h2 className="font-display text-5xl font-extrabold leading-tight tracking-tighter">
                            Welcome<br /> back.
                        </h2>
                        <p className="mt-4 max-w-md text-[hsl(215,20%,75%)]">
                            Sign in to manage your links, view real-time click analytics and
                            unlock advanced link controls.
                        </p>
                    </div>
                    <div className="font-mono text-xs text-[hsl(215,20%,55%)]">
                        © {new Date().getFullYear()} Splink.pro
                    </div>
                </aside>

                <main className="flex items-center justify-center p-6 sm:p-12">
                    <div className="w-full max-w-md">
                        <h1 className="font-display text-4xl font-bold tracking-tighter">Sign in</h1>
                        <p className="mt-2 text-sm text-muted-foreground">
                            New to Splink?{" "}
                            <Link to="/register" className="font-semibold text-primary hover:underline" data-testid="link-to-register">
                                Create an account
                            </Link>
                        </p>
                        {pendingId && (
                            <div className="mt-5 rounded-md border-2 border-[hsl(27,96%,55%)]/50 bg-[hsl(27,96%,55%)]/10 p-4 text-sm leading-relaxed text-foreground" data-testid="pending-notice">
                                <div className="flex items-start gap-2">
                                    <span className="text-lg leading-none">🎯</span>
                                    <div>
                                        <div className="font-semibold">
                                            After sign-in, we'll activate your preview short URL and QR code under your account.
                                        </div>
                                        <div className="mt-2 text-xs text-muted-foreground">
                                            Preview reservations last <strong className="text-foreground">30 minutes</strong>.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <Button
                            type="button"
                            variant="outline"
                            className="mt-8 h-11 w-full gap-3"
                            onClick={socialpulseLogin}
                            data-testid="socialpulse-login-btn"
                        >
                            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[hsl(27,96%,55%)] text-[10px] font-bold text-white">
                                SP
                            </span>
                            Continue with SocialPulse
                        </Button>

                        <Button
                            type="button"
                            variant="outline"
                            className="mt-3 h-11 w-full gap-3"
                            onClick={googleLogin}
                            data-testid="google-login-btn"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
                                <path fill="#FBBC05" d="M5.84 14.1A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.43.34-2.1V7.06H2.18A11 11 0 0 0 1 12c0 1.77.43 3.45 1.18 4.94l3.66-2.84z"/>
                                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.65l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
                            </svg>
                            Continue with Google
                        </Button>

                        <div className="relative my-8">
                            <Separator />
                            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-background px-3 text-xs uppercase tracking-widest text-muted-foreground">
                                or with email
                            </span>
                        </div>

                        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
                            <div>
                                <Label htmlFor="login-email">Email</Label>
                                <Input
                                    id="login-email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="mt-2 h-11"
                                    required
                                    data-testid="login-email-input"
                                />
                            </div>
                            <div>
                                <Label htmlFor="login-password">Password</Label>
                                <Input
                                    id="login-password"
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="mt-2 h-11"
                                    required
                                    data-testid="login-password-input"
                                />
                            </div>
                            {error && (
                                <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="login-error">
                                    {error}
                                </div>
                            )}
                            <Button
                                type="submit"
                                disabled={loading}
                                className="h-11 w-full bg-primary hover:bg-primary/90"
                                data-testid="login-submit-btn"
                            >
                                {loading ? "Signing in..." : "Sign in"}
                            </Button>
                        </form>
                    </div>
                </main>
            </div>
        </div>
    );
}
